// usePrompt.js
import { useContext, useEffect } from "react";
import { UNSAFE_NavigationContext as NavigationContext } from "react-router-dom";

export function usePrompt(message, when = true) {
    const navigator = useContext(NavigationContext)?.navigator;

    useEffect(() => {
        if (!when || !navigator) return;
        const confirmLeave = () => window.confirm(message);
        let leaveConfirmed = false;
        const onEditorLeave = event => {
            leaveConfirmed = confirmLeave();
            if (!leaveConfirmed) event.preventDefault();
        };
        window.addEventListener("ffls:before-editor-leave", onEditorLeave);

        // BrowserRouter's navigator does not expose history.block.
        const originals = {};
        let currentIndex = window.history.state?.idx;
        let restoring = null;
        let allowNextPop = false;
        for (const method of ["push", "replace", "go"]) {
            if (typeof navigator[method] !== "function") continue;
            originals[method] = navigator[method];
            navigator[method] = (...args) => {
                if (!leaveConfirmed && !confirmLeave()) return;
                leaveConfirmed = false;
                if (method === "go") allowNextPop = true;
                const result = originals[method].apply(navigator, args);
                currentIndex = window.history.state?.idx;
                return result;
            };
        }
        const onPopState = event => {
            const nextIndex = window.history.state?.idx;
            if (allowNextPop) {
                allowNextPop = false;
                currentIndex = nextIndex;
                return;
            }
            if (restoring !== null) {
                event.stopImmediatePropagation();
                const delta = restoring;
                restoring = null;
                if (confirmLeave()) {
                    allowNextPop = true;
                    window.history.go(delta);
                }
                return;
            }
            if (Number.isInteger(currentIndex) && Number.isInteger(nextIndex) && nextIndex !== currentIndex) {
                event.stopImmediatePropagation();
                restoring = nextIndex - currentIndex;
                window.history.go(-restoring);
            }
        };
        window.addEventListener("popstate", onPopState, true);
        return () => {
            for (const [method, original] of Object.entries(originals)) navigator[method] = original;
            window.removeEventListener("popstate", onPopState, true);
            window.removeEventListener("ffls:before-editor-leave", onEditorLeave);
        };
    }, [message, when, navigator]);
}
